import { useCallback, useEffect, useState } from 'react';

const COLLAPSED_KEY = 'emerge.sidebar.collapsed';

/**
 * Read during the initial state computation, never in an effect: restoring the
 * rail after first paint would flash the expanded sidebar on every load.
 * Storage access throws outright in some private-browsing modes, so every touch
 * of it is guarded.
 */
function readCollapsed(): boolean {
  try {
    return localStorage.getItem(COLLAPSED_KEY) === '1';
  } catch {
    return false;
  }
}

export function useSidebarCollapsed(): [boolean, () => void] {
  const [collapsed, setCollapsed] = useState(readCollapsed);

  useEffect(() => {
    try {
      localStorage.setItem(COLLAPSED_KEY, collapsed ? '1' : '0');
    } catch {
      // Persisting is a convenience; the session still works without it.
    }
  }, [collapsed]);

  const toggle = useCallback(() => setCollapsed((prev) => !prev), []);

  return [collapsed, toggle];
}

/*
 * How many overlays currently want the background frozen, and what to put back
 * when the last of them lets go.
 *
 * 🔴 **MODULE-LEVEL AND REF-COUNTED, AND THAT IS THE WHOLE FIX — reported
 * 2026-09-03 as "after voiding, deleting or editing the UI freezes and needs a
 * refresh, sometimes".**
 *
 * ⚠️ *SUPERSEDED: each caller captured `document.body.style.overflow` into its
 * own `previous` and wrote it back on cleanup.* That is correct for ONE overlay
 * and silently wrong for two, which is the ordinary case here rather than an
 * edge: **`ConfirmDialog` IS a `Modal`**, so every destructive action opens a
 * second lock on top of the form modal or drawer it was raised from.
 *
 *   1. the form modal opens      -> previous = ''       body = hidden
 *   2. the confirm opens over it -> previous = 'hidden' body = hidden
 *   3. both close in one commit  -> the modal restores '' ...
 *   4. ... then the confirm restores 'hidden'
 *
 * **The body is left `overflow: hidden` with nothing on screen holding it
 * there.** The page renders, every button still works and every click lands --
 * it simply cannot be scrolled, which reads as the app having frozen, and only
 * a reload clears it. That is why it was reported as intermittent: it needs two
 * overlays to overlap and to end in that order, so the same delete looks fine
 * from a list row and sticks when done from inside a form.
 *
 * ⚠️ **NOTHING IN THIS APP COULD HAVE CAUGHT IT.** No error, no warning, no
 * failed request, and `tsc` sees four correct-looking effects. The state lives
 * on `document.body` -- outside React entirely -- so no component renders
 * wrongly and no test of a component would fail.
 *
 * ⚠️ **The saved value is taken at the FIRST lock and restored only by the
 * LAST release.** Re-reading it on the second lock is what recorded 'hidden' as
 * the thing to go back to; the count is what stops an inner overlay's cleanup
 * unfreezing a page an outer one still holds.
 */
let scrollLocks = 0;
let overflowBeforeLock: string | null = null;

/**
 * Freezes background scrolling while a modal, drawer or full-screen panel is
 * open. Safe to nest -- see the note above, which is a bug report as much as a
 * comment.
 *
 * ⚠️ **Module state, so it is shared by every caller in the tab.** That is the
 * point: two components cannot agree on whose turn it is to unfreeze the page
 * unless they count against the same number. It is reset by the count reaching
 * zero, never by a caller, so a component that unmounts mid-flight still gives
 * its lock back through its own cleanup.
 */
export function useBodyScrollLock(locked: boolean): void {
  useEffect(() => {
    if (!locked) return;

    if (scrollLocks === 0) overflowBeforeLock = document.body.style.overflow;
    scrollLocks += 1;
    document.body.style.overflow = 'hidden';

    return () => {
      // `max(0, …)` rather than a bare decrement: a count that goes negative
      // never returns to zero, and the page would then stay frozen for the life
      // of the tab -- the very failure this exists to end, made permanent.
      scrollLocks = Math.max(0, scrollLocks - 1);
      if (scrollLocks === 0) {
        document.body.style.overflow = overflowBeforeLock ?? '';
        overflowBeforeLock = null;
      }
    };
  }, [locked]);
}

const GROUPS_KEY = 'emerge.sidebar.openGroups';

/*
 * `knownGroups` recorded which section labels a user had already seen, so that a
 * group added later could open itself once rather than hide behind a header
 * nobody had a reason to click. The cure was worse than the disease: a section
 * opening itself on a load the user did nothing to provoke is a nav that moves
 * under them. Every header is visible and one click from its contents either
 * way, and a closed section holding the current page still shows its marker —
 * so a new group is merely closed, not invisible. The behaviour is gone, and
 * the key it needed is swept up on read.
 */
const LEGACY_KNOWN_GROUPS_KEY = 'emerge.sidebar.knownGroups';

/**
 * The stored value is a JSON array of open section labels.
 *
 * Two older shapes exist in the wild and both must survive a read: the original
 * array (same shape, so it just works) and the accordion-era single JSON string
 * — `"Entities"` — which is read as an array of one. Anything else parses to
 * "nothing open" rather than throwing, so a hand-edited or half-written value
 * costs the user a closed nav, never a crash.
 */
function parseStoredGroups(raw: string | null): string[] {
  if (!raw) return [];
  try {
    const parsed: unknown = JSON.parse(raw);
    // Accordion-era value: one label, not a list.
    if (typeof parsed === 'string') return [parsed];
    if (Array.isArray(parsed)) return parsed.filter((v): v is string => typeof v === 'string');
    return [];
  } catch {
    return [];
  }
}

/**
 * The sections to start expanded — empty for none.
 *
 * Nothing stored means a first visit, and a first visit opens nothing: the nav
 * reads as a short list of section names rather than a wall of rows.
 *
 * A remembered label is only honoured if the nav still has a section by that
 * name. Renaming or removing a group would otherwise leave a preference that
 * matches nothing, which has to resolve to "not open" — never to a state where
 * a header says collapsed and no section can be reached. Filtering here rather
 * than at the call site also means the stale label is dropped from storage on
 * the very next write.
 */
function readOpenGroups(allLabels: readonly string[]): Set<string> {
  let stored: string[];
  try {
    stored = parseStoredGroups(localStorage.getItem(GROUPS_KEY));
    localStorage.removeItem(LEGACY_KNOWN_GROUPS_KEY);
  } catch {
    // Storage throws outright in some private-browsing modes.
    return new Set();
  }
  return new Set(stored.filter((label) => allLabels.includes(label)));
}

/**
 * Which sidebar sections are expanded. **Any number of them** — opening one
 * leaves the others alone. This was briefly an accordion; the single-open rule
 * was removed on request, because closing a section the user had opened, as a
 * side effect of them opening a different one, loses state they chose.
 *
 * A set rather than a list: membership is the only question ever asked of it,
 * and a set cannot represent the same label being open twice.
 *
 * The choice is remembered, so "all closed at the start" means the first visit,
 * not a state the user has to re-fix on every load. Read during initial state
 * so nothing flashes open then shut.
 */
export function useNavGroups(
  allLabels: readonly string[],
): [ReadonlySet<string>, (label: string) => void] {
  const [openGroups, setOpenGroups] = useState<Set<string>>(() => readOpenGroups(allLabels));

  useEffect(() => {
    try {
      localStorage.setItem(GROUPS_KEY, JSON.stringify([...openGroups]));
    } catch {
      // Persisting is a convenience; the session still works without it.
    }
  }, [openGroups]);

  const toggle = useCallback((label: string) => {
    // A new Set every time — mutating the held one would not re-render.
    setOpenGroups((prev) => {
      const next = new Set(prev);
      if (!next.delete(label)) next.add(label);
      return next;
    });
  }, []);

  return [openGroups, toggle];
}
